import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BasicInformation from '../components/BasicInformation';
import BudgetSection from '../components/BudgetSection';
import Button from 'shared/components/UC/Button';
import Alert from 'shared/components/UC/Alert';
import BackLink from '../components/BackLink';
import PageHeader from '../components/PageHeader';
import ContestInformation from '../components/ContestInformation';
import ProgressTracker from '../components/ProgressTracker';
import ProjectContent from '../components/ProjectContent';
import PublicationsSection from '../components/PublicationsSection';
import ReviewSubmission from '../components/ReviewSubmission';
import TeamInformation from '../components/TeamInformation';
import Toast from 'shared/components/Toast';
import { useUser } from '../contexts/AuthContext';
import { FormModeProvider } from '../contexts/FormModeContext';
import {
  ValidationErrorsProvider,
  useValidationErrors,
} from '../contexts/ValidationErrorsContext';
import adminService from '../services/adminService';
import applicationService from '../services/applicationService';
import { SectionBasedApplication, SectionStatus, SectionType } from '../types';
import {
  cleanupOldNavigationStates,
  clearNavigationState,
  getNavigationState,
  isNavigatingBackwards,
  saveNavigationState,
} from '../utils/navigationStorage';
import { checkAllSectionsCompletion } from '../utils/sectionCompletion';
import {
  buildCompleteFormState,
  extractSectionData,
  getDynamicSectionType,
  getStaticStepId,
} from '../utils/sectionMapping';
import { processValidationErrors } from '../utils/validationErrorProcessor';
import { hasBlockingSaveErrors } from '../utils/validationUtils';
import { formatSavedAt } from '../utils/formatSavedAt';
import styles from './DynamicApplication.module.css';

// Component mapping for section types
const sectionComponents: Record<
  string,
  React.ComponentType<{
    readOnly?: boolean;
    isActive?: boolean | undefined;
    // Contenido de la convocatoria (config.contestInfo del template); solo lo
    // consume ContestInformation, el resto lo ignora.
    contestInfo?: import('../types').ContestInfoContent | null;
  }>
> = {
  'contest-info': ContestInformation,
  basic: BasicInformation,
  content: ProjectContent,
  team: TeamInformation,
  budget: BudgetSection,
  publications: PublicationsSection,
  review: ReviewSubmission,
};

// Steps will be dynamically loaded from backend sections

const DynamicApplicationInner: React.FC<{ isAdmin?: boolean }> = ({
  isAdmin = false,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const { setValidationErrors } = useValidationErrors();
  const [application, setApplication] =
    useState<SectionBasedApplication | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  } | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  // Origen del último guardado: la barra distingue automático vs. manual.
  const [lastSaveSource, setLastSaveSource] = useState<'auto' | 'manual'>('auto');
  // Los campos de react-hook-form son no-controlados: sin esta suscripción la
  // página no se re-renderiza al editar y la barra nunca recalcularía
  // "Cambios sin guardar" (hasStepChanges). Tick con debounce para no
  // re-renderizar en cada tecla.
  const [, setFormTick] = useState(0);
  const [needsPIInitialSave, setNeedsPIInitialSave] = useState(false);
  const [steps, setSteps] = useState<{ id: string; title: string }[]>([]);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [stepLastSavedData, setStepLastSavedData] = useState<
    Record<string, any>
  >({});
  const [shouldValidateCurrentStep, setShouldValidateCurrentStep] =
    useState(false);

  // Get role-based dashboard URL, prioritizing location state
  const getDashboardUrl = useCallback(() => {
    // Check if we have a "from" path in location state
    const fromPath = (location.state as any)?.from;
    if (fromPath && typeof fromPath === 'string') {
      return fromPath;
    }
    // Fallback to role-based dashboard
    if (!user) return '/dashboard';
    return `/${user.role}/dashboard`;
  }, [user, location.state]);

  const methods = useForm({
    mode: 'onTouched', // Only show errors for fields user has touched
    reValidateMode: 'onChange', // Re-validate on every change
    criteriaMode: 'all', // Report all validation errors, not just the first one
    defaultValues: {},
  });

  // Re-render (con debounce) ante cualquier cambio del formulario, para que
  // el estado de guardado de la barra sticky refleje los cambios sin guardar.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const subscription = methods.watch(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setFormTick((t) => t + 1), 250);
    });
    return () => {
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [methods]);

  const { reset, trigger, clearErrors, setValue, getValues } = methods;

  // Helper function to mark fields as touched and trigger validation for a specific step
  // This ensures errors are displayed when navigating backwards
  const triggerAndShowAllErrorsForStep = useCallback(
    async (stepId: string) => {
      // Get the field list for this specific step
      const sectionFields: Record<string, string[]> = {
        'contest-info': [], // Static info, no form fields
        basic: [
          'projectTitle',
          'interdisciplinaryProject',
          'transdisciplinaryProject',
          'genderApproachProject',
          'genderApproachOptions',
          'openScience',
          'rcer',
          'keywords',
          'evaluationArea',
          'oecdArea',
          'oecdSubarea',
          'oecdDiscipline',
          'sdgGoals',
          'executionRegions',
        ],
        content: ['executiveSummary', 'projectDocument'],
        team: ['principalInvestigator', 'coInvestigators'],
        budget: ['budget'],
        publications: [
          'ipCvData',
          'coInvestigators',
          'suggestedReviewers',
          'hasConflictOfInterest',
          'conflictOfInterestList',
          'principalInvestigatorDoiFile',
          'coInvestigatorDoiFile',
          'institutionCommitmentLetters',
        ],
        review: [
          'finalReportsUC',
          'finalReportsANID',
          'duplicateFunding',
          'otherIncompatibilities',
          'otherIncompatibilitiesDetails',
          'noIncompatibilities',
          'informationDeclaration',
          'status',
          'submittedAt',
        ],
      };

      const fieldsToValidate = sectionFields[stepId] || [];

      if (fieldsToValidate.length === 0) {
        return;
      }

      // Get all form values
      const allValues = getValues();

      // Mark only the fields for this step as touched
      const markFieldsTouched = (fieldPath: string, obj: any, prefix = '') => {
        Object.keys(obj).forEach((key) => {
          const fullKey = prefix ? `${prefix}.${key}` : key;
          const value = obj[key];

          if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            !(value instanceof Date) &&
            !(value instanceof File)
          ) {
            // Recursively mark nested objects (but not Dates or Files)
            markFieldsTouched(fieldPath, value, fullKey);
          } else {
            // Mark this field as touched
            // @ts-ignore - setValue typing is too strict for dynamic field paths
            setValue(fullKey, value, {
              shouldTouch: true,
              shouldValidate: false,
            });
          }
        });
      };

      // Mark each field in the step as touched
      fieldsToValidate.forEach((fieldName) => {
        const value = (allValues as any)[fieldName];
        if (
          value &&
          typeof value === 'object' &&
          !Array.isArray(value) &&
          !(value instanceof Date) &&
          !(value instanceof File)
        ) {
          // For nested objects like principalInvestigator
          markFieldsTouched(fieldName, value, fieldName);
        } else {
          // For simple fields
          // @ts-ignore - setValue typing is too strict for dynamic field paths
          setValue(fieldName, value, {
            shouldTouch: true,
            shouldValidate: false,
          });
        }
      });

      // Trigger validation for all fields in the step
      // @ts-ignore - trigger accepts field names as array
      await trigger(fieldsToValidate);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getValues, setValue, trigger]
  );

  // Function to update the last saved state for a step
  const updateStepLastSavedData = useCallback(
    (stepId: string) => {
      const fullFormData = methods.getValues();
      const sectionSpecificData = extractSectionData(stepId, fullFormData);

      // For publications section, also include team data for change detection
      let dataToStore = sectionSpecificData;
      if (stepId === 'publications') {
        const teamData = extractSectionData('team', fullFormData);
        dataToStore = { ...sectionSpecificData, teamData };
      }

      setStepLastSavedData((prev) => ({
        ...prev,
        [stepId]: JSON.parse(JSON.stringify(dataToStore)), // Deep copy to avoid reference issues
      }));
    },
    [methods]
  );

  // Function to check if current step has changes since last save
  const hasStepChanges = useCallback(
    (stepId: string): boolean => {
      const fullFormData = methods.getValues();
      const currentData = extractSectionData(stepId, fullFormData);

      // For publications section, also include team data for change detection
      let dataToCompare = currentData;
      if (stepId === 'publications') {
        const teamData = extractSectionData('team', fullFormData);
        dataToCompare = { ...currentData, teamData };
      }

      const lastSavedData = stepLastSavedData[stepId];

      if (!lastSavedData) {
        return true; // If no saved data, assume changes exist
      }

      return JSON.stringify(dataToCompare) !== JSON.stringify(lastSavedData);
    },
    [methods, stepLastSavedData]
  );

  const fetchApplication = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      const data = isAdmin
        ? await adminService.getApplicationById(id)
        : await applicationService.getApplicationById(id);
      setApplication(data as SectionBasedApplication);

      // Set read-only mode based on application status and user ownership
      setIsReadOnly(data.status !== 'draft' || user?.id !== data.userId);

      // Generate steps from backend sections
      if (data.formTemplate.sections) {
        const dynamicSteps = data.formTemplate.sections
          .sort((a, b) => a.order - b.order)
          .map((section) => ({
            id: getStaticStepId(section.sectionType),
            title: section.title,
          }));
        setSteps(dynamicSteps);
      }

      if (data.answers.lastSaved) {
        setLastSaved(new Date(data.answers.lastSaved));
      }

      // Initialize form with complete form state from all sections
      const completeFormState = buildCompleteFormState(
        data.answers.sectionAnswers
      );

      // Initialize Principal Investigator data safely if not already present
      if (
        user &&
        user.userProfile &&
        (!completeFormState.principalInvestigator?.name ||
          !completeFormState.principalInvestigator?.email ||
          !completeFormState.principalInvestigator?.run)
      ) {
        completeFormState.principalInvestigator = {
          ...completeFormState.principalInvestigator,
          name:
            completeFormState.principalInvestigator?.name || user.name || '',
          email:
            completeFormState.principalInvestigator?.email || user.email || '',
          run:
            completeFormState.principalInvestigator?.run ||
            user.userProfile?.run ||
            '',
          affiliation:
            completeFormState.principalInvestigator?.affiliation ||
            'Pontificia Universidad Católica de Chile',
          facultadUC:
            completeFormState.principalInvestigator?.facultadUC ||
            user.userProfile?.facultadUC ||
            '',
          unidadAcademicaUC:
            completeFormState.principalInvestigator?.unidadAcademicaUC ||
            user.userProfile?.unidadAcademica ||
            '',
          doctorateYear:
            completeFormState.principalInvestigator?.doctorateYear ||
            user.userProfile?.doctorateYear ||
            new Date().getFullYear(),
          type:
            completeFormState.principalInvestigator?.type ||
            user.userProfile?.researcherType ||
            'emerging',
          role: 'IP',
        };
        setNeedsPIInitialSave(true);
      }

      reset(completeFormState, {
        keepErrors: true,
        keepTouched: true,
        keepDirty: true,
      });

      // Initialize the saved state for all steps based on backend data
      if (data.formTemplate.sections) {
        const initialSavedData: Record<string, any> = {};
        data.formTemplate.sections.forEach((section) => {
          const stepId = getStaticStepId(section.sectionType);
          const sectionData = extractSectionData(stepId, completeFormState);
          initialSavedData[stepId] = JSON.parse(JSON.stringify(sectionData));
        });
        setStepLastSavedData(initialSavedData);
      }
    } catch (err) {
      console.error('Error fetching application:', err);
      setToast({
        message:
          err instanceof Error
            ? err.message
            : 'Error desconocido al cargar la aplicación',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [id, reset, user, isAdmin]);

  // Save function for section-based data
  const saveSectionToBackend = useCallback(
    async (
      sectionUpdates:
        | { sectionData: any; sectionType: SectionType }
        | { sectionData: any; sectionType: SectionType }[],
      skipValidationCheck = false
    ) => {
      if (!application || !id) return;

      // Check for blocking validation errors unless explicitly skipped
      if (
        !skipValidationCheck &&
        hasBlockingSaveErrors(methods.formState.errors)
      ) {
        console.warn(
          'Manual save blocked due to validation errors that indicate invalid data'
        );
        setToast({
          message:
            'No se puede guardar debido a errores de validación. Por favor, corrige los errores antes de guardar.',
          type: 'error',
        });
        return { success: false, reason: 'validation_errors' };
      }

      try {
        setSaving(true);

        // Handle both single section and multiple sections
        const updates = Array.isArray(sectionUpdates)
          ? sectionUpdates
          : [sectionUpdates];

        // Update section answers and status for all sections
        let updatedSectionAnswers = { ...application.answers.sectionAnswers };
        let updatedSectionStatus = { ...application.answers.sectionStatus };

        updates.forEach(({ sectionData, sectionType }) => {
          updatedSectionAnswers[sectionType] = sectionData;
          updatedSectionStatus[sectionType] = 'in_progress' as SectionStatus;
        });

        const updatedApp = await applicationService.updateApplication(id, {
          answers: {
            sectionAnswers: updatedSectionAnswers,
            sectionStatus: updatedSectionStatus,
          },
        });
        setApplication(updatedApp);
        setLastSaved(new Date());
        return { success: true };
      } catch (err) {
        console.error('Error saving application:', err);
        setToast({
          message: 'Error al guardar. Intenta de nuevo.',
          type: 'error',
        });
        return { success: false, reason: 'save_error' };
      } finally {
        setSaving(false);
      }
    },
    [application, id, methods.formState.errors, setToast]
  );

  // Helper function for cross-section saving (handles publications section specially)
  const saveCurrentStep = useCallback(
    async (
      stepId: string,
      source: 'auto' | 'manual' = 'auto'
    ): Promise<{ success: boolean; reason?: string }> => {
      const fullFormData = methods.getValues();

      // Special handling for publications section (needs cross-section save)
      if (stepId === 'publications') {
        // Prepare both sets of data
        const publicationsData = extractSectionData(
          'publications',
          fullFormData
        );
        const teamData = extractSectionData('team', fullFormData);

        // Use generalized function with multiple sections
        const result = await saveSectionToBackend([
          { sectionData: publicationsData, sectionType: 'PROJECT_ATTACHMENTS' },
          { sectionData: teamData, sectionType: 'PROJECT_TEAM' },
        ]);
        if (result?.success !== false) setLastSaveSource(source);
        return result || { success: true };
      } else {
        // Normal single-section save for other steps
        const sectionType = getDynamicSectionType(stepId);
        const sectionSpecificData = extractSectionData(stepId, fullFormData);
        const result = await saveSectionToBackend({
          sectionData: sectionSpecificData,
          sectionType,
        });
        if (result?.success !== false) setLastSaveSource(source);
        return result || { success: true };
      }
    },
    [methods, saveSectionToBackend]
  );

  // Wrapper function for FormModeContext that conforms to the expected interface
  const onSaveWrapper = useCallback(
    async (sectionData: any, sectionType: SectionType) => {
      // Special handling for publications section auto-save
      if (sectionType === 'PROJECT_ATTACHMENTS') {
        // This is an auto-save from publications section, use the proper cross-section save
        await saveCurrentStep('publications');
      } else {
        await saveSectionToBackend({ sectionData, sectionType });
        setLastSaveSource('auto');
      }
    },
    [saveSectionToBackend, saveCurrentStep]
  );

  // Submit function for final submission
  const submitApplication = useCallback(async () => {
    if (!application || !id) return;

    try {
      setSubmitting(true);
      setValidationErrors([]); // Clear previous validation errors

      // First, save the current step if there are any unsaved changes
      const currentStep = steps[currentStepIndex];
      if (hasStepChanges(currentStep.id)) {
        const saveResult = await saveCurrentStep(currentStep.id);
        if (saveResult?.success === false) {
          setToast({
            message:
              'Error al guardar antes del envío. Por favor, inténtalo de nuevo.',
            type: 'error',
          });
          return;
        }
        // Update the saved state after successful save
        updateStepLastSavedData(currentStep.id);
      }

      // Build complete answers object with current form data
      const currentData = methods.getValues();
      const sectionType = getDynamicSectionType(currentStep.id);

      // Extract only the section-specific data (not all form data)
      const sectionSpecificData = extractSectionData(
        currentStep.id,
        currentData
      );

      // Update section answers with current data
      const updatedSectionAnswers = {
        ...application.answers.sectionAnswers,
        [sectionType]: sectionSpecificData,
      };

      const updatedSectionStatus = {
        ...application.answers.sectionStatus,
        [sectionType]: 'completed' as SectionStatus,
      };

      const submitData = {
        answers: {
          applicationNumber: application.applicationNumber,
          sectionAnswers: updatedSectionAnswers,
          sectionStatus: updatedSectionStatus,
          status: 'submitted' as const,
          lastSaved: new Date(),
          complianceConfirmed:
            (currentData as any).informationDeclaration || false,
        },
      };

      try {
        await applicationService.submitApplication(id, submitData);

        // Success - clear navigation state and redirect to dashboard
        if (id) {
          clearNavigationState(id);
        }
        window.scrollTo(0, 0);
        navigate(getDashboardUrl());
      } catch (submitError) {
        // Check if this is a validation error response
        const errorData = (submitError as any).response || submitError;

        // Reload application from backend to ensure data is in sync
        await fetchApplication();

        // Check if this is a structured validation error response
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const processedErrors = processValidationErrors(errorData.errors);
          setValidationErrors(processedErrors);

          // Don't navigate away from the current section - keep user on review page

          // Don't set error message when we have structured validation errors
        } else {
          // Fallback for non-structured errors
          setToast({
            message: errorData.message || 'Error al enviar la postulación',
            type: 'error',
          });
        }
        return; // Don't throw, just return to show the errors
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al enviar la postulación',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    application,
    id,
    methods,
    steps,
    currentStepIndex,
    navigate,
    getDashboardUrl,
    setValidationErrors,
    hasStepChanges,
    saveCurrentStep,
    updateStepLastSavedData,
    setToast,
    fetchApplication,
  ]);

  // Load complete form data when section changes
  useEffect(() => {
    if (application && steps.length > 0) {
      // Build complete form state from all sections to handle cross-section dependencies
      const completeFormState = buildCompleteFormState(
        application.answers.sectionAnswers
      );

      // Prefill Principal Investigator data if not already present
      if (
        user &&
        (!completeFormState.principalInvestigator?.name ||
          !completeFormState.principalInvestigator?.email)
      ) {
        completeFormState.principalInvestigator = {
          ...completeFormState.principalInvestigator,
          name:
            completeFormState.principalInvestigator?.name || user.name || '',
          email:
            completeFormState.principalInvestigator?.email || user.email || '',
          run:
            completeFormState.principalInvestigator?.run ||
            user.userProfile?.run ||
            '',
          affiliation:
            completeFormState.principalInvestigator?.affiliation ||
            'Pontificia Universidad Católica de Chile',
          facultadUC:
            completeFormState.principalInvestigator?.facultadUC ||
            user.userProfile?.facultadUC ||
            '',
          unidadAcademicaUC:
            completeFormState.principalInvestigator?.unidadAcademicaUC ||
            user.userProfile?.unidadAcademica ||
            '',
          doctorateYear:
            completeFormState.principalInvestigator?.doctorateYear ||
            user.userProfile?.doctorateYear ||
            new Date().getFullYear(),
          type:
            completeFormState.principalInvestigator?.type ||
            user.userProfile?.researcherType ||
            'emerging',
          role: 'IP',
        };
      }

      // Only reset if not currently saving to prevent overwriting user input
      if (!saving) {
        // Merge current form values with backend data, prioritizing current values
        const currentValues = methods.getValues();
        const mergedValues = { ...completeFormState, ...currentValues };
        reset(mergedValues, {
          keepErrors: true,
          keepTouched: true,
          keepDirty: true,
        });
      }
    }
  }, [currentStepIndex, application, reset, steps, user, saving, methods]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  // Initialize navigation state and restore last step on mount
  useEffect(() => {
    if (!id || !application) return;

    // Cleanup old navigation states
    cleanupOldNavigationStates();

    // Restore last visited step from localStorage
    const savedState = getNavigationState(id);
    if (
      savedState &&
      savedState.currentStep !== undefined &&
      steps.length > 0
    ) {
      // Only restore if the saved step is valid for the current form
      if (savedState.currentStep < steps.length) {
        setCurrentStepIndex(savedState.currentStep);
      }
    } else if (steps.length > 0) {
      // No saved state - check if application is submitted
      if (application.status !== 'draft') {
        // For submitted applications, navigate to the last step (review)
        const lastStepIndex = steps.length - 1;
        setCurrentStepIndex(lastStepIndex);
        saveNavigationState(id, lastStepIndex);
      } else {
        // For draft applications, initialize with step 0
        saveNavigationState(id, 0);
      }
    }
  }, [id, application, steps.length]);

  // Update last saved data when step changes (this represents the backend state)
  useEffect(() => {
    if (steps.length > 0 && currentStepIndex < steps.length && application) {
      const currentStep = steps[currentStepIndex];
      updateStepLastSavedData(currentStep.id);
    }
  }, [currentStepIndex, steps, application, updateStepLastSavedData]);

  // Save PI data to backend if it was just initialized
  useEffect(() => {
    if (needsPIInitialSave && !loading && application) {
      const savePIData = async () => {
        try {
          setSaving(true);
          const currentFormData = methods.getValues();
          const teamSectionData = extractSectionData('team', currentFormData);
          const teamSectionType = getDynamicSectionType('team');
          await saveSectionToBackend({
            sectionData: teamSectionData,
            sectionType: teamSectionType,
          });
          setNeedsPIInitialSave(false);
        } catch (err) {
          console.error('Error saving initial PI data:', err);
          // Don't show error to user - this is an automatic save
          setNeedsPIInitialSave(false);
        } finally {
          setSaving(false);
        }
      };

      savePIData();
    }
  }, [needsPIInitialSave, loading, application, methods, saveSectionToBackend]);

  // Trigger validation after navigating backwards (after component renders)
  useEffect(() => {
    if (
      shouldValidateCurrentStep &&
      steps.length > 0 &&
      currentStepIndex < steps.length
    ) {
      const currentStep = steps[currentStepIndex];

      // Wait for component to render and register fields
      setTimeout(async () => {
        await triggerAndShowAllErrorsForStep(currentStep.id);
        setShouldValidateCurrentStep(false);
      }, 100);
    }
  }, [
    shouldValidateCurrentStep,
    currentStepIndex,
    steps,
    triggerAndShowAllErrorsForStep,
  ]);

  // Scroll to top when step changes
  useEffect(() => {
    if (window.scrollY > 460) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStepIndex]);

  const handleNext = async () => {
    if (currentStepIndex < steps.length - 1 && !isReadOnly) {
      const currentStep = steps[currentStepIndex];

      // Only save if there are changes
      if (hasStepChanges(currentStep.id)) {
        try {
          setNavigating(true);
          const result = await saveCurrentStep(currentStep.id);
          if (
            result?.success === false &&
            result?.reason === 'validation_errors'
          ) {
            // Error message already shown by saveSectionToBackend
            return;
          }
          // Update the saved state after successful save
          updateStepLastSavedData(currentStep.id);
        } catch (err) {
          console.error('Error saving before navigation:', err);
          setToast({
            message: 'Error al guardar. Por favor, inténtalo de nuevo.',
            type: 'error',
          });
          return; // Don't navigate if save fails
        } finally {
          setNavigating(false);
        }
      }
    }

    if (currentStepIndex < steps.length - 1) {
      const newStepIndex = currentStepIndex + 1;

      // Check if we're going forward (not backward)
      const isGoingForward = id && !isNavigatingBackwards(id, newStepIndex);

      // Clear errors when navigating forward to prevent showing stale validation errors
      if (isGoingForward) {
        clearErrors();
      }

      // Save navigation state (previousStep = currentStepIndex, currentStep = newStepIndex)
      if (id) {
        saveNavigationState(id, newStepIndex);
      }

      setCurrentStepIndex(newStepIndex);
    }
  };

  const handlePrevious = async () => {
    if (currentStepIndex > 0 && !isReadOnly) {
      const currentStep = steps[currentStepIndex];

      // Only save if there are changes
      if (hasStepChanges(currentStep.id)) {
        try {
          setNavigating(true);
          const sectionType = getDynamicSectionType(currentStep.id);
          const fullFormData = methods.getValues();
          const sectionSpecificData = extractSectionData(
            currentStep.id,
            fullFormData
          );
          const result = await saveSectionToBackend({
            sectionData: sectionSpecificData,
            sectionType,
          });
          if (
            result?.success === false &&
            result?.reason === 'validation_errors'
          ) {
            // Error message already shown by saveSectionToBackend
            return;
          }
          // Update the saved state after successful save
          updateStepLastSavedData(currentStep.id);
        } catch (err) {
          console.error('Error saving before navigation:', err);
          setToast({
            message: 'Error al guardar. Por favor, inténtalo de nuevo.',
            type: 'error',
          });
          return; // Don't navigate if save fails
        } finally {
          setNavigating(false);
        }
      }
    }

    if (currentStepIndex > 0) {
      const newStepIndex = currentStepIndex - 1;

      // Check if navigating backwards BEFORE saving navigation state
      // isNavigatingBackwards checks: newStepIndex < state.currentStep
      // At this point, state.currentStep is still the old value (currentStepIndex)
      // So this checks: newStepIndex < currentStepIndex, which is always true when going back
      const isGoingBack = id && isNavigatingBackwards(id, newStepIndex);

      // Check if going backwards - if so, set flag to validate AFTER step changes
      if (isGoingBack) {
        setShouldValidateCurrentStep(true);
      } else {
      }

      // Save navigation state to localStorage
      if (id) {
        saveNavigationState(id, newStepIndex);
      }

      setCurrentStepIndex(newStepIndex);
    }
  };

  const handleStepClick = async (stepIndex: number) => {
    if (stepIndex !== currentStepIndex && !isReadOnly) {
      const currentStep = steps[currentStepIndex];

      // Only save if there are changes
      if (hasStepChanges(currentStep.id)) {
        try {
          setNavigating(true);
          const sectionType = getDynamicSectionType(currentStep.id);
          const fullFormData = methods.getValues();
          const sectionSpecificData = extractSectionData(
            currentStep.id,
            fullFormData
          );
          await saveSectionToBackend({
            sectionData: sectionSpecificData,
            sectionType,
          });
          // Update the saved state after successful save
          updateStepLastSavedData(currentStep.id);
        } catch (err) {
          console.error('Error saving before navigation:', err);
          setToast({
            message: 'Error al guardar. Por favor, inténtalo de nuevo.',
            type: 'error',
          });
          return; // Don't navigate if save fails
        } finally {
          setNavigating(false);
        }
      }
    }

    // Check if navigating backwards BEFORE saving navigation state
    // isNavigatingBackwards checks: stepIndex < state.currentStep
    const isGoingBack = id && isNavigatingBackwards(id, stepIndex);

    // Check if going backwards - if so, set flag to validate AFTER step changes
    if (isGoingBack) {
      setShouldValidateCurrentStep(true);
    } else {
      // Clear errors when navigating forward to prevent showing stale validation errors
      clearErrors();
    }

    // Save navigation state to localStorage
    if (id) {
      saveNavigationState(id, stepIndex);
    }

    // Update current step
    setCurrentStepIndex(stepIndex);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Cargando formulario...</div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Postulación no encontrada</div>
        {isAdmin ? (
          <BackLink label="Ir a inicio" onClick={() => navigate('/admin/dashboard')} />
        ) : (
          <BackLink to="mis postulaciones" onClick={() => navigate(getDashboardUrl())} />
        )}
      </div>
    );
  }

  const CurrentStepComponent =
    steps.length > 0 && steps[currentStepIndex]
      ? sectionComponents[steps[currentStepIndex].id]
      : null;

  const isActive = application?.formTemplate?.isActive;

  return (
    <div className={styles.pageWrapper}>
      <PageHeader
        sticky
        titleLevel="detail"
        title={`Postulación: ${application.applicationNumber}`}
        backLink={
          isAdmin
            ? {
                label: 'Volver al concurso',
                onClick: () =>
                  navigate(`/admin/dashboard/${application.formTemplateId}`),
              }
            : { to: 'mis postulaciones', onClick: () => navigate(getDashboardUrl()) }
        }
      />
      <div className={styles.container}>
        {isReadOnly && (
          <div className={styles.submittedAlert}>
            <Alert variant="success">
              Postulación enviada. Esta postulación no puede ser modificada.
            </Alert>
          </div>
        )}
        <div className={styles.formContainer}>

        <FormModeProvider
          mode="dynamic"
          applicationId={id}
          onSave={isReadOnly ? undefined : onSaveWrapper}
          autoSaveEnabled={!isReadOnly}
          isLoading={saving}
          lastSaved={lastSaved}
          createdAt={application.createdAt}
          submittedAt={application.submittedAt}
        >
          <FormProvider {...methods}>
            <div className={styles.stepperWrap}>
              <ProgressTracker
                steps={steps}
                currentStep={currentStepIndex}
                onStepClick={handleStepClick}
                disabled={navigating || saving}
              />
            </div>

            <div className={styles.formContent}>
              <div className={styles.stepContent}>
                {CurrentStepComponent && (
                  <CurrentStepComponent
                    readOnly={isReadOnly}
                    isActive={isActive}
                    contestInfo={
                      application?.formTemplate?.sections?.find(
                        (s) => s.sectionType === 'APPLICATION_INFORMATION'
                      )?.config?.contestInfo ?? null
                    }
                  />
                )}
              </div>

              <div className={styles.navigationButtons}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handlePrevious}
                  disabled={currentStepIndex === 0 || navigating || saving}
                >
                  {navigating ? 'Guardando...' : 'Anterior'}
                </Button>

                <div className={styles.navRightGroup}>
                  {/* Estado de guardado + Guardar cambios, homologado con la
                      barra del panel de evaluación (antes: SaveIndicator del
                      kit sobre el contenido + botón "Guardar Borrador"). */}
                  {!isReadOnly &&
                    (() => {
                      const stepDirty = steps[currentStepIndex]
                        ? hasStepChanges(steps[currentStepIndex].id)
                        : false;
                      const busy = saving || navigating;
                      return (
                        <>
                          <span
                            className={`${styles.saveStatus} ${
                              busy
                                ? styles.saveStatusSaving
                                : stepDirty
                                  ? styles.saveStatusDirty
                                  : styles.saveStatusSaved
                            }`}
                          >
                            {!stepDirty || busy ? (
                              <span
                                className={`${styles.saveIcon} ${
                                  busy ? styles.saveIconSaving : ''
                                }`}
                                aria-hidden="true"
                              >
                                {busy ? '⟳' : '✓'}
                              </span>
                            ) : null}
                            {(() => {
                              if (busy) return 'Guardando...';
                              if (stepDirty) return 'Cambios sin guardar';
                              const label =
                                lastSaveSource === 'manual'
                                  ? 'Cambios guardados'
                                  : 'Guardado automáticamente';
                              return lastSaved
                                ? `${label} · ${formatSavedAt(lastSaved)}`
                                : label;
                            })()}
                          </span>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={async () => {
                              const currentStep = steps[currentStepIndex];
                              const result = await saveCurrentStep(
                                currentStep.id,
                                'manual'
                              );
                              if (
                                result?.success === false &&
                                result?.reason === 'validation_errors'
                              ) {
                                // Error message already shown by saveSectionToBackend
                              }
                            }}
                            disabled={busy || !stepDirty}
                          >
                            {busy ? 'Guardando...' : 'Guardar cambios'}
                          </Button>
                        </>
                      );
                    })()}

                  {!isReadOnly && currentStepIndex === steps.length - 1 ? (
                    (() => {
                      const formData = methods.watch();
                      const allSectionsComplete =
                        checkAllSectionsCompletion(formData);
                      const isFormComplete =
                        allSectionsComplete &&
                        (formData as any).informationDeclaration;

                      // Incompleta: no se muestra botón ni mensaje aquí; el
                      // estado ya lo comunica la alerta "Postulación incompleta"
                      // del Resumen. El botón "Enviar" aparece solo cuando el
                      // envío está habilitado.
                      return isFormComplete ? (
                        <Button
                          type="button"
                          variant="cta"
                          onClick={submitApplication}
                          disabled={
                            !isActive || saving || submitting || navigating
                          }
                        >
                          {submitting ? 'Enviando...' : 'Enviar Postulación'}
                        </Button>
                      ) : null;
                    })()
                  ) : currentStepIndex < steps.length - 1 ? (
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleNext}
                      disabled={navigating || saving}
                    >
                      {navigating ? 'Guardando...' : 'Siguiente'}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </FormProvider>
        </FormModeProvider>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      </div>
    </div>
  );
};

// Main wrapper component with validation errors provider
const DynamicApplication: React.FC<{ isAdmin?: boolean }> = ({
  isAdmin = false,
}) => {
  return (
    <ValidationErrorsProvider>
      <DynamicApplicationInner isAdmin={isAdmin} />
    </ValidationErrorsProvider>
  );
};

export default DynamicApplication;
